import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useMotionPreferences } from '../context/MotionPreferencesContext'

/**
 * useScrollReveal Hook
 *
 * Reveals elements with smooth animations as they enter the viewport.
 * Uses IntersectionObserver for performance.
 *
 * CONFIGURATION:
 * @param {Object} options
 * @param {number} options.threshold - How much of element must be visible (0-1)
 * @param {string} options.rootMargin - Margin around root (e.g., '-50px')
 * @param {boolean} options.once - Only trigger once (default: true)
 * @param {number} options.delay - Delay before animation in ms
 *
 * @returns {Object} { ref, isVisible, hasAnimated }
 *
 * Usage:
 * ```jsx
 * const { ref, isVisible } = useScrollReveal({ threshold: 0.2 })
 * return <div ref={ref} className={isVisible ? 'visible' : 'hidden'}>...</div>
 * ```
 */
export const useScrollReveal = ({
  threshold = 0.15,
  rootMargin = '0px 0px -50px 0px',
  once = true,
  delay = 0
} = {}) => {
  const ref = useRef(null)
  const [isVisible, setIsVisible] = useState(false)
  const [hasAnimated, setHasAnimated] = useState(false)
  const { reducedMotion } = useMotionPreferences()

  useEffect(() => {
    // If reduced motion, show immediately
    if (reducedMotion) {
      setIsVisible(true)
      setHasAnimated(true)
      return
    }

    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (delay > 0) {
            setTimeout(() => {
              setIsVisible(true)
              setHasAnimated(true)
            }, delay)
          } else {
            setIsVisible(true)
            setHasAnimated(true)
          }

          if (once) {
            observer.unobserve(element)
          }
        } else if (!once) {
          setIsVisible(false)
        }
      },
      { threshold, rootMargin }
    )

    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [threshold, rootMargin, once, delay, reducedMotion])

  return { ref, isVisible, hasAnimated }
}

/**
 * useStaggeredReveal Hook
 *
 * Reveals multiple elements with staggered timing.
 * Perfect for lists, grids, or sequential content.
 *
 * @param {number} itemCount - Number of items to animate
 * @param {Object} options - Same as useScrollReveal plus:
 * @param {number} options.staggerDelay - Delay between each item (ms)
 *
 * @returns {Object} { containerRef, getItemProps }
 *
 * Usage:
 * ```jsx
 * const { containerRef, getItemProps } = useStaggeredReveal(items.length)
 * return (
 *   <div ref={containerRef}>
 *     {items.map((item, i) => (
 *       <div {...getItemProps(i)}>...</div>
 *     ))}
 *   </div>
 * )
 * ```
 */
export const useStaggeredReveal = (
  itemCount,
  {
    threshold = 0.1,
    rootMargin = '0px 0px -30px 0px',
    staggerDelay = 100,
    baseDelay = 0
  } = {}
) => {
  const containerRef = useRef(null)
  const [isContainerVisible, setIsContainerVisible] = useState(false)
  const { reducedMotion } = useMotionPreferences()

  useEffect(() => {
    if (reducedMotion) {
      setIsContainerVisible(true)
      return
    }

    const element = containerRef.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsContainerVisible(true)
          observer.unobserve(element)
        }
      },
      { threshold, rootMargin }
    )

    observer.observe(element)

    return () => observer.disconnect()
  }, [threshold, rootMargin, reducedMotion])

  const getItemProps = useCallback((index) => {
    const delay = reducedMotion ? 0 : baseDelay + index * staggerDelay

    return {
      style: {
        opacity: isContainerVisible ? 1 : 0,
        transform: isContainerVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: reducedMotion
          ? 'none'
          : `opacity 0.5s ease ${delay}ms, transform 0.5s ease ${delay}ms`
      },
      'data-index': index
    }
  }, [isContainerVisible, staggerDelay, baseDelay, reducedMotion])

  return {
    containerRef,
    isContainerVisible,
    getItemProps
  }
}

/**
 * ScrollReveal Component Wrapper
 *
 * A component alternative to the hook for simpler use cases.
 *
 * Usage:
 * ```jsx
 * <ScrollReveal animation="fadeUp" delay={200}>
 *   <YourComponent />
 * </ScrollReveal>
 * ```
 */
export const ScrollReveal = ({
  children,
  animation = 'fadeUp',
  threshold = 0.15,
  delay = 0,
  className = '',
  as: Component = 'div',
  ...props
}) => {
  const { ref, isVisible } = useScrollReveal({ threshold, delay })
  const { reducedMotion } = useMotionPreferences()

  const animations = {
    fadeUp: {
      hidden: { opacity: 0, transform: 'translateY(30px)' },
      visible: { opacity: 1, transform: 'translateY(0)' }
    },
    fadeDown: {
      hidden: { opacity: 0, transform: 'translateY(-30px)' },
      visible: { opacity: 1, transform: 'translateY(0)' }
    },
    fadeLeft: {
      hidden: { opacity: 0, transform: 'translateX(-30px)' },
      visible: { opacity: 1, transform: 'translateX(0)' }
    },
    fadeRight: {
      hidden: { opacity: 0, transform: 'translateX(30px)' },
      visible: { opacity: 1, transform: 'translateX(0)' }
    },
    scale: {
      hidden: { opacity: 0, transform: 'scale(0.95)' },
      visible: { opacity: 1, transform: 'scale(1)' }
    },
    fade: {
      hidden: { opacity: 0 },
      visible: { opacity: 1 }
    }
  }

  const currentAnimation = animations[animation] || animations.fadeUp
  const state = isVisible ? 'visible' : 'hidden'

  const style = reducedMotion
    ? {}
    : {
        ...currentAnimation[state],
        transition: 'opacity 0.6s ease, transform 0.6s ease'
      }

  return (
    <Component
      ref={ref}
      className={className}
      style={style}
      {...props}
    >
      {children}
    </Component>
  )
}

export default useScrollReveal
